import { createSlice } from "@reduxjs/toolkit";
import { API_STATUS } from "../../../constants/apiStatus";
import {
  loginAction,
  logoutAction,
  verifyTokenAction,
  registerAction,
  forgotPasswordAction,
  resetPasswordAction,
} from "./authSliceActions";
import { authSliceInitialState } from "./authSliceInitialState";

const initialState = authSliceInitialState;

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
    },
    resetAuthState: () => initialState,
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(loginAction.fulfilled, (state, action) => {
      state.apiStatus.login = API_STATUS.COMPLETED;
      state.isAuthenticated = true;
      state.user = action.payload.admin || action.payload.user;
      state.token = action.payload.token;
      state.error = null;

      // Save to localStorage
      localStorage.setItem("authToken", action.payload.token);
      localStorage.setItem(
        "authUser",
        JSON.stringify(action.payload.admin || action.payload.user)
      );
      // Maintain backward compatibility with older sessionStorage based guard
      try {
        sessionStorage.setItem("adminAuthenticated", "true");
        sessionStorage.setItem("adminToken", action.payload.token);
      } catch (_) {}
    });
    builder.addCase(loginAction.pending, (state) => {
      state.apiStatus.login = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(loginAction.rejected, (state, action) => {
      state.apiStatus.login = API_STATUS.FAILED;
      state.error = action.payload;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;

      // Clear localStorage on login failure
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
    });

    // Logout
    builder.addCase(logoutAction.fulfilled, (state) => {
      state.apiStatus.logout = API_STATUS.COMPLETED;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.error = null;

      // Clear localStorage
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
      try {
        sessionStorage.removeItem("adminAuthenticated");
        sessionStorage.removeItem("adminToken");
      } catch (_) {}
    });
    builder.addCase(logoutAction.pending, (state) => {
      state.apiStatus.logout = API_STATUS.STARTED;
    });
    builder.addCase(logoutAction.rejected, (state, action) => {
      state.apiStatus.logout = API_STATUS.FAILED;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
    });

    // Verify Token
    builder.addCase(verifyTokenAction.fulfilled, (state, action) => {
      state.apiStatus.verify = API_STATUS.COMPLETED;
      state.isAuthenticated = true;
      state.user = action.payload.admin || action.payload.user;
      state.error = null;
      try {
        sessionStorage.setItem("adminAuthenticated", "true");
      } catch (_) {}
    });
    builder.addCase(verifyTokenAction.pending, (state) => {
      state.apiStatus.verify = API_STATUS.STARTED;
    });
    builder.addCase(verifyTokenAction.rejected, (state, action) => {
      state.apiStatus.verify = API_STATUS.FAILED;
      state.error = action.payload;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;

      // Clear localStorage if token verification fails
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
    });

    // Register
    builder.addCase(registerAction.fulfilled, (state, action) => {
      state.apiStatus.register = API_STATUS.COMPLETED;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.error = null;
    });
    builder.addCase(registerAction.pending, (state) => {
      state.apiStatus.register = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(registerAction.rejected, (state, action) => {
      state.apiStatus.register = API_STATUS.FAILED;
      state.error = action.payload;
    });

    // Forgot Password
    builder.addCase(forgotPasswordAction.fulfilled, (state) => {
      state.apiStatus.forgotPassword = API_STATUS.COMPLETED;
      state.error = null;
    });
    builder.addCase(forgotPasswordAction.pending, (state) => {
      state.apiStatus.forgotPassword = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(forgotPasswordAction.rejected, (state, action) => {
      state.apiStatus.forgotPassword = API_STATUS.FAILED;
      state.error = action.payload;
    });

    // Reset Password
    builder.addCase(resetPasswordAction.fulfilled, (state) => {
      state.apiStatus.resetPassword = API_STATUS.COMPLETED;
      state.error = null;
    });
    builder.addCase(resetPasswordAction.pending, (state) => {
      state.apiStatus.resetPassword = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(resetPasswordAction.rejected, (state, action) => {
      state.apiStatus.resetPassword = API_STATUS.FAILED;
      state.error = action.payload;
    });
  },
});

export const { clearError, setUser, resetAuthState } = authSlice.actions;
export default authSlice.reducer;
